import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { api } from '../lib/api';
import { SophiaAvatar } from '../components/SophiaAvatar';
import { LanguageSelector } from '../components/LanguageSelector';
import { VoiceRecorderButton, VoiceMessageBubble, RecordingPulse } from '../components/VoiceRecorder';
import { ShareWisdomButton } from '../components/ShareWisdomCard';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mic, Send, Volume2, VolumeX, Plus, Copy, Check, ImageIcon } from 'lucide-react';

export default function ConversationPage() {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  
  // Voice recorder hook
  const {
    isRecording,
    audioLevel,
    recordingTime,
    transcript: liveTranscript,
    error: recordingError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder(language);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [sessionId, setSessionId] = useState(() => {
    const saved = localStorage.getItem('sophia_session_id');
    return saved || `conv-${Date.now()}`;
  });
  const scrollRef = useRef(null);
  const currentAudioRef = useRef(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const copyMessage = async (text, index) => {
    const formattedText = `"${text}" — Sophia Einstein`;
    try {
      await navigator.clipboard.writeText(formattedText);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = formattedText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    }
  };

  const shareMessage = async (text) => {
    const formattedText = `"${text}" — Sophia Einstein\n@sophia_einstein_the_wise`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Sophia Einstein',
          text: formattedText,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Share cancelled');
        }
      }
    }
  };

  // Save session ID
  useEffect(() => {
    localStorage.setItem('sophia_session_id', sessionId);
  }, [sessionId]);

  // Load saved voice preference
  useEffect(() => {
    const savedVoice = localStorage.getItem('sophia_voice_enabled');
    if (savedVoice !== null) {
      setVoiceEnabled(savedVoice === 'true');
    }
  }, []);

  // Save voice preference
  useEffect(() => {
    localStorage.setItem('sophia_voice_enabled', voiceEnabled.toString());
  }, [voiceEnabled]);

  // Load saved conversation
  useEffect(() => {
    api.getChatHistory(sessionId).then(({ data }) => {
      if (data && data.length > 0) {
        setMessages(data.map(m => ({
          role: m.role,
          content: m.content,
          audio_url: m.audio_url || null,
          isVoiceMessage: m.isVoiceMessage || false,
        })));
      }
    }).catch(() => {});
  }, [sessionId]);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        const el = scrollRef.current;
        el.scrollTop = el.scrollHeight;
      }
    }, 150);
  }, []);

  const playAudio = useCallback((audioUrl) => {
    if (!audioUrl) return null;
    
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    setIsSpeaking(true);
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    
    audio.onended = () => {
      setIsSpeaking(false);
      currentAudioRef.current = null;
    };
    audio.onerror = () => {
      setIsSpeaking(false);
      currentAudioRef.current = null;
    };
    
    audio.play().catch((e) => {
      console.error('Audio play error:', e);
      setIsSpeaking(false);
    });
    
    return audio;
  }, []);

  const sendMessage = async (text, isVoice = false) => {
    if (!text.trim() || isLoading) return;
    const userMsg = { role: 'user', content: text, isVoiceMessage: isVoice };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    scrollToBottom();

    try {
      const { data } = await api.sendConversation(text, sessionId, language);
      const sophiaMsg = { 
        role: 'assistant', 
        content: data.response, 
        audio_url: data.audio_url || null,
        isVoiceMessage: false
      };
      setMessages((prev) => [...prev, sophiaMsg]);
      scrollToBottom();

      if (voiceEnabled && data.audio_url) {
        playAudio(data.audio_url);
      }
    } catch (err) {
      console.error('Conversation error:', err);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Hmm, my thoughts wandered off. Try again, darling.' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const relistenMessage = (audioUrl) => {
    if (!audioUrl) return;
    playAudio(audioUrl);
  };

  const startNewConversation = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setMessages([]);
    const newId = `conv-${Date.now()}`;
    setSessionId(newId);
  };

  const toggleVoice = () => {
    if (voiceEnabled) {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      setIsSpeaking(false);
    }
    setVoiceEnabled(!voiceEnabled);
  };

  // Handle voice recording completion
  const handleVoiceRecordingComplete = async () => {
    const result = await stopRecording();
    if (result.transcript && result.transcript.trim()) {
      sendMessage(result.transcript, true);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      {/* Header */}
      <motion.header
        className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border/50 backdrop-blur-xl bg-background/90 sticky top-0 z-30"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="flex items-center gap-2">
          <Button
            data-testid="conversation-back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Button
            data-testid="conversation-new-chat-btn"
            variant="outline"
            size="sm"
            onClick={startNewConversation}
            className="rounded-full text-xs gap-1 border-sophia-pink-500/30 text-sophia-pink-500 hover:bg-sophia-pink-500/10"
          >
            <Plus className="w-3 h-3" />
            {t('conversation.newChat')}
          </Button>
        </div>
        <h1 className="font-display text-lg sm:text-xl font-semibold">{t('conversation.title')}</h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="conversation-voice-toggle"
            onClick={toggleVoice}
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              voiceEnabled 
                ? 'bg-sophia-pink-500/15 text-sophia-pink-500' 
                : 'bg-muted text-muted-foreground'
            }`}
            title={voiceEnabled 
              ? (language === 'de' ? 'Stimme ausschalten' : 'Turn voice off') 
              : (language === 'de' ? 'Stimme einschalten' : 'Turn voice on')
            }
          >
            {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <LanguageSelector />
        </div>
      </motion.header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4" ref={scrollRef}>
        <div className="max-w-2xl mx-auto space-y-5" data-testid="conversation-messages">
          {/* Initial Sophia greeting */}
          {messages.length === 0 && (
            <motion.div
              className="flex flex-col items-center pt-8 pb-12 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <SophiaAvatar variant="pink" size="lg" isSpeaking={isSpeaking} />
              <p className="text-muted-foreground text-center font-body text-lg max-w-sm">
                {t('conversation.placeholder')}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {voiceEnabled ? (
                  <>
                    <Volume2 className="w-3 h-3" />
                    <span>{language === 'de' ? 'Stimme an' : 'Voice on'}</span>
                  </>
                ) : (
                  <>
                    <VolumeX className="w-3 h-3" />
                    <span>{language === 'de' ? 'Stimme aus' : 'Voice off'}</span>
                  </>
                )}
              </div>
            </motion.div>
          )}

          <AnimatePresence mode="popLayout">
            {messages.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} items-end gap-2`}
              >
                {msg.role === 'assistant' && (
                  <SophiaAvatar variant="pink" size="sm" animate={false} />
                )}
                <div className="flex flex-col gap-1 max-w-[80%]">
                  <VoiceMessageBubble 
                    text={msg.content}
                    isUser={msg.role === 'user'}
                    isVoiceMessage={msg.isVoiceMessage}
                    language={language}
                  />
                  {/* Action buttons for Sophia messages */}
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-1 ml-1">
                      {msg.audio_url && (
                        <motion.button
                          data-testid={`relisten-btn-${i}`}
                          onClick={() => relistenMessage(msg.audio_url)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body text-sophia-pink-500 hover:bg-sophia-pink-500/10 transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Volume2 className="w-3 h-3" />
                          {t('conversation.relisten')}
                        </motion.button>
                      )}
                      <motion.button
                        data-testid={`copy-btn-${i}`}
                        onClick={() => copyMessage(msg.content, i)}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {copiedIndex === i ? (
                          <>
                            <Check className="w-3 h-3 text-green-500" />
                            <span className="text-green-500">{language === 'de' ? 'Kopiert' : 'Copied'}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            {language === 'de' ? 'Kopieren' : 'Copy'}
                          </>
                        )}
                      </motion.button>
                      {/* Share as Wisdom Card */}
                      <motion.button
                        data-testid={`share-card-btn-${i}`}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-body text-sophia-pink-500 hover:bg-sophia-pink-500/10 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <ShareWisdomButton quote={msg.content} language={language} variant="icon" className="w-6 h-6" />
                      </motion.button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {isLoading && (
            <motion.div
              className="flex items-end gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <SophiaAvatar variant="pink" size="sm" animate={false} />
              <div className="bg-card rounded-2xl rounded-tl-sm p-4 border border-border/50">
                <div className="flex gap-1.5">
                  <motion.div className="w-2 h-2 bg-sophia-pink-500 rounded-full" animate={{ y: [0, -8, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0 }} />
                  <motion.div className="w-2 h-2 bg-sophia-pink-500 rounded-full" animate={{ y: [0, -8, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }} />
                  <motion.div className="w-2 h-2 bg-sophia-pink-500 rounded-full" animate={{ y: [0, -8, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }} />
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Speaking indicator */}
      <AnimatePresence>
        {isSpeaking && (
          <motion.div
            className="flex items-center justify-center gap-2 py-2 bg-sophia-pink-500/10"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Volume2 className="w-4 h-4 text-sophia-pink-500 animate-pulse" />
            <span className="text-sm text-sophia-pink-500 font-body">{t('conversation.speaking')}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recording indicator (shows live transcript) */}
      <AnimatePresence>
        {isRecording && liveTranscript && (
          <motion.div
            className="px-4 sm:px-6 py-2 bg-sophia-pink-500/5 border-t border-sophia-pink-500/20"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="max-w-2xl mx-auto">
              <p className="text-sm text-sophia-pink-500/80 font-body italic truncate">
                "{liveTranscript}"
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Input area */}
      <motion.div
        className="px-4 sm:px-6 py-4 border-t border-border/50 bg-background/90"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {isRecording ? (
              <motion.div
                key="recording"
                className="flex items-center justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <VoiceRecorderButton
                  isRecording={isRecording}
                  audioLevel={audioLevel}
                  recordingTime={recordingTime}
                  transcript={liveTranscript}
                  onStartRecording={startRecording}
                  onStopRecording={handleVoiceRecordingComplete}
                  onCancelRecording={cancelRecording}
                  language={language}
                />
              </motion.div>
            ) : (
              <motion.div
                key="input"
                className="flex items-center gap-3"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                {/* Mic button with pulsing effect when ready */}
                <motion.button
                  data-testid="conversation-mic-btn"
                  onClick={startRecording}
                  disabled={isLoading}
                  className="relative w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 bg-card border border-border text-muted-foreground hover:text-sophia-pink-500 hover:border-sophia-pink-500/50 hover:bg-sophia-pink-500/5 disabled:opacity-40"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <RecordingPulse isRecording={false} />
                  <Mic className="w-5 h-5" />
                </motion.button>

                <input
                  data-testid="conversation-text-input"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage(input)}
                  placeholder={t('conversation.placeholder')}
                  className="flex-1 h-12 px-5 rounded-full bg-card border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sophia-pink-500/50 font-body text-sm"
                />

                <motion.button
                  data-testid="conversation-send-btn"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || isLoading}
                  className="w-12 h-12 rounded-full bg-sophia-pink-500 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sophia-pink-700 transition-colors shrink-0"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Send className="w-5 h-5" />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Recording error message */}
          <AnimatePresence>
            {recordingError && (
              <motion.p
                className="text-center text-xs text-red-500 mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {recordingError}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
